/**
 * CoverageSummary — the client-facing accountability guarantee (F3).
 *
 * "N answered from your data · M need your input" with an expandable list
 * of gaps, each carrying a human-readable reason derived from the
 * machine-readable fail-closed failure codes.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, AlertCircle, Send } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { createGapRequest } from '@/api/endpoints';
import { cn } from '@/lib/utils';
import { useSessionCoverage } from '../hooks/useSessionCoverage';
import type { CoverageItem } from '@/types/api.types';

/** Map fail-closed failure codes to client-facing copy. */
const GAP_REASON_LABELS: Record<string, string> = {
  missing_data: 'Not in your Vault yet',
  llm_fallback_requires_review: 'AI draft — needs your verification',
  llm_generated: 'AI draft — needs your verification',
  gap_language_detected: 'Answer indicates missing information',
  no_match: 'No similar approved answer found',
  partial_vault_match: 'Partial Vault match — review recommended',
  ethics_check_failed: 'Flagged by safety checks',
  llm_circuit_breaker: 'AI drafting temporarily unavailable',
  llm_cost_limit: 'AI drafting limit reached',
  llm_disabled: 'AI drafting disabled',
  processing_error: 'Processing error — retry or answer manually',
  input_invalid: 'Question text too short to process',
};

function gapReasonLabel(item: CoverageItem): string {
  return (item.gap_reason && GAP_REASON_LABELS[item.gap_reason]) || 'Needs your input';
}

export interface CoverageSummaryProps {
  sessionId: string | null;
  className?: string;
}

/** One explained gap with the F8 "Request from colleague" tracked-ask flow. */
function GapRow({ item, sessionId }: { item: CoverageItem; sessionId: string | null }) {
  const [isAsking, setIsAsking] = useState(false);
  const [email, setEmail] = useState('');
  const [mailto, setMailto] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  const submit = async () => {
    if (!sessionId || !email) return;
    setIsSending(true);
    try {
      const created = await createGapRequest({
        session_id: sessionId,
        response_id: item.question_id,
        question_text: item.question_text,
        gap_reason: item.gap_reason,
        assignee_email: email,
      });
      setMailto(created.mailto);
      setIsAsking(false);
    } catch {
      // Calm UI: keep the form open; the user can retry.
    } finally {
      setIsSending(false);
    }
  };

  return (
    <li className="flex flex-wrap items-start gap-2 text-sm">
      <Badge variant="outline" className="mt-0.5 shrink-0 whitespace-nowrap">
        {gapReasonLabel(item)}
      </Badge>
      <span className="min-w-0 flex-1 text-muted-foreground line-clamp-2">
        {item.question_text}
      </span>
      {mailto ? (
        <span className="flex shrink-0 items-center gap-2">
          <Badge variant="outline" className="text-tier-exact border-tier-exact">
            Tracked
          </Badge>
          <a className="text-apx-accent underline" href={mailto}>
            Open email
          </a>
        </span>
      ) : isAsking ? (
        <span className="flex shrink-0 items-center gap-1">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@yourfirm.com"
            className="h-7 w-52 text-xs"
          />
          <Button size="sm" className="h-7" onClick={submit} disabled={isSending || !email}>
            <Send className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setIsAsking(true)}
          className="shrink-0 text-xs text-muted-foreground underline hover:text-foreground"
        >
          Request from colleague
        </button>
      )}
    </li>
  );
}

export function CoverageSummary({ sessionId, className }: CoverageSummaryProps) {
  const { data, isLoading, isError } = useSessionCoverage(sessionId);
  const [isExpanded, setIsExpanded] = useState(false);

  // Calm UI: never block review on the summary; hide on load/error.
  if (isLoading || isError || !data) {
    return null;
  }

  const gaps = data.items.filter((item) => item.state === 'gap');

  return (
    <Card className={cn('border-l-4 border-l-apx-accent', className)} data-testid="coverage-summary">
      <CardContent className="py-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-tier-exact" aria-hidden />
              <span className="font-semibold">{data.answered_count}</span>
              <span className="text-muted-foreground">answered from your data</span>
            </span>
            <span className="text-muted-foreground" aria-hidden>
              ·
            </span>
            <span className="flex items-center gap-1.5">
              <AlertCircle className="h-4 w-4 text-tier-generated" aria-hidden />
              <span className="font-semibold">{data.gap_count}</span>
              <span className="text-muted-foreground">need your input</span>
            </span>
            {data.unclassified_count > 0 && (
              <Badge variant="destructive">{data.unclassified_count} unclassified</Badge>
            )}
          </div>
          {gaps.length > 0 && (
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              className="flex shrink-0 items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronRight className="h-4 w-4" aria-hidden />
              )}
              {isExpanded ? 'Hide gaps' : 'Why?'}
            </button>
          )}
        </div>

        {isExpanded && gaps.length > 0 && (
          <ul className="mt-3 space-y-2 border-t pt-3">
            {gaps.map((item) => (
              <GapRow key={item.question_id} item={item} sessionId={sessionId} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
